import React, { useEffect, useRef, useState } from 'react'
import { BsX, BsFilterLeft } from 'react-icons/bs'
import {
  EMPTY_FILTERS,
  filtersAreActive,
  getOperatorsForType,
  makeCondition,
  makeTopN,
} from '../../services/dataFilters'
import S from './DataFilters.module.scss'

function ConditionRow({ condition, columns, dataTypes, onChange, onRemove }) {
  const colType = dataTypes[condition.column] ?? 'string'
  const operators = getOperatorsForType(colType)
  const isActive = condition.value !== ''

  const handleColumnChange = (column) => {
    const newType = dataTypes[column] ?? 'string'
    const ops = getOperatorsForType(newType)
    const opStillValid = ops.some(o => o.value === condition.operator)
    onChange({ column, operator: opStillValid ? condition.operator : ops[0].value, value: '' })
  }

  return (
    <div className={[S.conditionRow, isActive ? S.conditionActive : ''].join(' ')}>
      <select
        className={S.select}
        value={condition.column}
        onChange={e => handleColumnChange(e.target.value)}
      >
        {columns.map(col => <option key={col} value={col}>{col}</option>)}
      </select>

      <select
        className={S.selectOperator}
        value={condition.operator}
        onChange={e => onChange({ operator: e.target.value })}
      >
        {operators.map(op => (
          <option key={op.value} value={op.value}>{op.label}</option>
        ))}
      </select>

      <input
        className={S.valueInput}
        type={colType === 'number' ? 'number' : 'text'}
        placeholder="value"
        value={condition.value}
        onChange={e => onChange({ value: e.target.value })}
      />

      <button className={S.removeBtn} onClick={onRemove} title="Remove">
        <BsX />
      </button>
    </div>
  )
}

function TopNRow({ topN, columns, onChange, onRemove }) {
  return (
    <div className={[S.conditionRow, S.conditionActive].join(' ')}>
      <span className={S.topNLabel}>Top</span>

      <input
        className={S.topNInput}
        type="number"
        min="1"
        max="9999"
        value={topN.n}
        onChange={e => onChange({ n: Math.max(1, Number(e.target.value) || 1) })}
      />

      <span className={S.topNLabel}>rows by</span>

      <select
        className={S.select}
        value={topN.column}
        onChange={e => onChange({ column: e.target.value })}
      >
        {columns.map(col => <option key={col} value={col}>{col}</option>)}
      </select>

      <select
        className={S.selectOperator}
        value={topN.direction}
        onChange={e => onChange({ direction: e.target.value })}
      >
        <option value="desc">highest</option>
        <option value="asc">lowest</option>
      </select>

      <button className={S.removeBtn} onClick={onRemove} title="Remove">
        <BsX />
      </button>
    </div>
  )
}

function useAnimatedCount(value) {
  const [animKey, setAnimKey] = useState(0)
  const prevRef = useRef(value)

  useEffect(() => {
    if (prevRef.current !== value) {
      setAnimKey(k => k + 1)
      prevRef.current = value
    }
  }, [value])

  return animKey
}

export default function DataFilters({ data, dataFilters, setDataFilters, filteredRowCount }) {
  if (!data) return null

  const { conditions, topN } = dataFilters
  const columns = Object.keys(data.dataTypes)
  const totalRows = data.dataset.length
  const isActive = filtersAreActive(dataFilters)
  const hasRules = conditions.length > 0 || topN != null
  const noResults = isActive && filteredRowCount === 0

  const countAnimKey = useAnimatedCount(filteredRowCount)

  const addCondition = () => {
    const firstCol = columns[0] ?? ''
    setDataFilters(prev => ({
      ...prev,
      conditions: [...prev.conditions, makeCondition(firstCol, data.dataTypes)],
    }))
  }

  const updateCondition = (id, patch) => {
    setDataFilters(prev => ({
      ...prev,
      conditions: prev.conditions.map(c => c.id === id ? { ...c, ...patch } : c),
    }))
  }

  const removeCondition = (id) => {
    setDataFilters(prev => ({
      ...prev,
      conditions: prev.conditions.filter(c => c.id !== id),
    }))
  }

  const addTopN = () => {
    setDataFilters(prev => ({ ...prev, topN: makeTopN(columns[0] ?? '') }))
  }

  const updateTopN = (patch) => {
    setDataFilters(prev => ({ ...prev, topN: { ...prev.topN, ...patch } }))
  }

  const removeTopN = () => {
    setDataFilters(prev => ({ ...prev, topN: null }))
  }

  const clearAll = () => setDataFilters(EMPTY_FILTERS)

  const removedCount = totalRows - filteredRowCount

  return (
    <div className={S.filterBar}>
      <div className={S.filterHeader}>
        <span className={S.filterLabel}>
          <BsFilterLeft className={S.filterIcon} />
          Filter data for chart
        </span>

        <div className={S.filterActions}>
          <button className={S.addBtn} onClick={addCondition}>
            + Condition
          </button>
          {topN == null && (
            <button className={S.addBtn} onClick={addTopN}>
              + Top N
            </button>
          )}
        </div>

        {isActive && (
          <span
            key={countAnimKey}
            className={[S.rowCount, S.rowCountAnimated].join(' ')}
          >
            {filteredRowCount.toLocaleString()} / {totalRows.toLocaleString()} rows
          </span>
        )}

        {isActive && (
          <button className={S.clearBtn} onClick={clearAll}>
            Clear all
          </button>
        )}
      </div>

      {hasRules && (
        <div className={S.filterRules}>
          {conditions.map(cond => (
            <ConditionRow
              key={cond.id}
              condition={cond}
              columns={columns}
              dataTypes={data.dataTypes}
              onChange={patch => updateCondition(cond.id, patch)}
              onRemove={() => removeCondition(cond.id)}
            />
          ))}

          {topN && (
            <TopNRow
              topN={topN}
              columns={columns}
              onChange={updateTopN}
              onRemove={removeTopN}
            />
          )}

          {isActive && (
            <div className={[S.resultsStrip, noResults ? S.resultsEmpty : S.resultsOk].join(' ')}>
              {noResults ? (
                <>
                  <span className={S.resultsIcon}>⚠</span>
                  <span>No rows match these filters — chart will be empty</span>
                </>
              ) : (
                <>
                  <span className={S.resultsIcon}>✓</span>
                  <span>
                    <span className={S.resultsCount}>{filteredRowCount.toLocaleString()}</span>
                    {' '}of {totalRows.toLocaleString()} rows match
                    {removedCount > 0 && (
                      <span style={{ color: 'var(--gray-500)', marginLeft: 4 }}>
                        ({removedCount.toLocaleString()} filtered out)
                      </span>
                    )}
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
